/** Settings > Sounds: master switch, volume, and each kind of sound on or off. */
import { useT } from '../i18n';
import { play, SOUND_KINDS, useSounds, type SoundEvent, type SoundKind } from '../sound/sounds';
import { Icon, IconBtn, Range, Switch } from '../ui/kit';
import { SetGroup, SetRow } from './Settings';

/** The sound a kind's preview button plays. */
const PREVIEW: Record<SoundKind, SoundEvent> = {
  join: 'join',
  leave: 'leave',
  move: 'move',
  peerJoin: 'peerJoin',
  peerLeave: 'peerLeave',
  mute: 'mute',
  deafen: 'deafen',
  ptt: 'pttOn',
  connection: 'disconnected',
  poke: 'poke',
  message: 'message',
  call: 'ring',
  streamStart: 'streamStart',
  streamEnd: 'streamEnd',
  viewerJoin: 'viewerJoin',
  viewerLeave: 'viewerLeave',
};

export function SoundsSection() {
  const { t, fmt } = useT();
  const prefs = useSounds((s) => s.prefs);
  const setPrefs = useSounds((s) => s.setPrefs);
  const setKind = useSounds((s) => s.setKind);
  return (
    <>
      <h2>{t('ns.snd.title')}</h2>
      <SetGroup>
        <SetRow label={t('ns.snd.enabled')} sub={t('ns.snd.enabledSub')}>
          <Switch
            checked={prefs.enabled}
            label={t('ns.snd.enabled')}
            onChange={(v) => setPrefs({ enabled: v })}
          />
        </SetRow>
        <SetRow label={t('ns.snd.volume')}>
          <Range
            min={0}
            max={100}
            step={5}
            value={Math.round(prefs.volume * 100)}
            disabled={!prefs.enabled}
            aria-label={t('ns.snd.volume')}
            aria-valuetext={fmt.num(prefs.volume, { style: 'percent', maximumFractionDigits: 0 })}
            onChange={(e) => setPrefs({ volume: Number(e.target.value) / 100 })}
            onPointerUp={() => play('join', { preview: true })}
          />
          <span className="nx-num ns-range-v">
            {fmt.num(prefs.volume, { style: 'percent', maximumFractionDigits: 0 })}
          </span>
        </SetRow>
      </SetGroup>
      <SetGroup title={t('ns.snd.which')}>
        {SOUND_KINDS.map((k) => (
          <SetRow key={k} label={t(`ns.snd.k.${k}`)}>
            <IconBtn
              icon="play"
              label={t('ns.snd.preview', { name: t(`ns.snd.k.${k}`) })}
              onClick={() => play(PREVIEW[k], { preview: true })}
            />
            <Switch
              checked={!prefs.off[k]}
              disabled={!prefs.enabled}
              label={t(`ns.snd.k.${k}`)}
              onChange={(v) => setKind(k, v)}
            />
          </SetRow>
        ))}
      </SetGroup>
      <p className="nx-set-after">
        <Icon name="info" />
        <span>{t('ns.snd.deafNote')}</span>
      </p>
    </>
  );
}
